import { Injectable, InjectionToken, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import { PreferencesService } from '../../services/preferences/preferences.service';

export interface ClientProfileConfiguration {
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly freeFormName?: string;
}

export const CLIENT_PROFILE_CONFIGURATION = new InjectionToken<ClientProfileConfiguration>('clientProfileConfiguration');

/**
 * Client Profile Service
 *
 * Service is used for client profile info
 *
 */
@Injectable({
  providedIn: 'root'
})
export class ClientProfileService {
  private readonly preferencesService = inject(PreferencesService);
  private readonly translateService = inject(TranslateService);

  readonly onPreferredNameChange: BehaviorSubject<string>;

  private preferredName: string;
  private readonly clientProfile: ClientProfileConfiguration = {};

  constructor() {
    const clientProfileConfiguration = inject<ClientProfileConfiguration>(CLIENT_PROFILE_CONFIGURATION, { optional: true });

    this.preferredName = this.preferencesService.getPreloadedUserPreference('PREFERRED_NAME');
    this.onPreferredNameChange = new BehaviorSubject<string>(this.preferredName);
    if (clientProfileConfiguration !== null) {
      this.clientProfile = { ...clientProfileConfiguration };
    }
  }

  setPreferredName(preferredName: string): void {
    this.preferredName = preferredName;
    this.onPreferredNameChange.next(this.preferredName);
  }

  /**
   * Get client's full name
   * Will return preferred name if available
   *
   * @param useNameCase change name to name case
   * @returns
   */
  getFullName(useNameCase: boolean = false): string {
    if (this.isValidPreferredName()) {
      return this.preferredName;
    }
    return this.getOriginalFullName(useNameCase);
  }

  /**
   * Get client's first name
   * Will return preferred name if available
   *
   * @param useNameCase change name to name case
   * @returns
   */
  getFirstName(useNameCase: boolean = false): string {
    if (this.isValidPreferredName()) {
      return this.preferredName;
    }
    const firstName = this.clientProfile.firstName;
    return this.formatName(firstName, useNameCase);
  }

  /**
   * Get client's last name
   *
   * @param useNameCase change name to name case
   * @returns
   */
  getLastName(useNameCase: boolean = false): string {
    const lastName = this.clientProfile.lastName;
    return this.formatName(lastName, useNameCase);
  }

  /**
   * Get client's original name
   *
   * @param useNameCase change name to name case
   * @returns
   */
  getOriginalName(useNameCase: boolean = false): string {
    return this.getOriginalFullName(useNameCase);
  }

  /**
   * Check preferred name
   *
   * @returns
   */
  private isValidPreferredName(): boolean {
    return this.preferredName !== undefined && this.preferredName !== '';
  }

  /**
   * Format name to name case if required
   *
   * @param name
   * @param useNameCase change name to name case
   * @returns
   */
  private formatName(name: string, useNameCase: boolean): string {
    let formattedName = '';
    if (name !== undefined && name.length > 0) {
      if (useNameCase) {
        formattedName = name.charAt(0).toUpperCase();
        formattedName += name.length > 1 ? name.substring(1).toLowerCase() : '';
      } else {
        formattedName = name;
      }
    }
    return formattedName;
  }

  /**
   * Get name from client profile
   *
   * @param useNameCase change name to name case
   * @returns
   */
  private getOriginalFullName(useNameCase: boolean = false): string {
    let result: string = this.formatName(this.clientProfile.firstName, useNameCase);
    if (typeof this.clientProfile.middleName === 'string') {
      result += ' ' + this.formatName(this.clientProfile.middleName, useNameCase);
    }
    if (typeof this.clientProfile.lastName === 'string') {
      result += ' ' + this.formatName(this.clientProfile.lastName, useNameCase);
    }
    result = result.trim();
    if (typeof result === 'string' && result.length > 1) {
      return result;
    }
    const freeFormName = this.clientProfile.freeFormName;
    if (typeof freeFormName === 'string' && freeFormName.length > 0) {
      return freeFormName;
    }
    return this.translateService.instant('tdWb.clientProfile.DEFAULT_CLIENT_NAME');
  }
}
